import { orderAdvancePercentage } from './advancePercentage';
import { codeLabel } from '../../utils/codeLabel';
import { PayeeResolution } from './PayeeResolution';
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Detention, OrderMoneyStaffSummary, PaymentSlaState } from '../../api/types';
import { getPaymentTerms } from '../../api/endpoints/payments';
import { InvoiceBox } from '../invoices/InvoiceBox';
import { formatMoney, parsePaise } from './allocation';
import { copyText, partnerBillCopyText } from './copyText';
import { formatIstDate, formatIstDateTime } from './paymentDates';
import { OrderLabel } from './OrderLabel';
import { StatusChip } from './StatusChip';
import { SlaChip } from './SlaChip';
import { OrderPaymentHistory } from './OrderPaymentHistory';
import { OrderPaymentDialogs } from './OrderPaymentDialogs';
import styles from './Payments.module.css';
import { Button } from '../../components/ui/Button';

const money = (value: string) => formatMoney(parsePaise(value));
const reasons: Record<string, string> = {
  NO_BANK: 'No bank details',
  KYC_PENDING: 'KYC pending',
  KYC_SUBMITTED: 'KYC submitted',
  KYC_REJECTED: 'KYC rejected',
  NO_FLEET_MANAGER: 'No partner',
};
const maxZero = (value: bigint) => (value > 0n ? value : 0n);
function LedgerRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.ledgerRow}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
function ChargeBreakdown({
  summary,
  side,
}: {
  summary: OrderMoneyStaffSummary;
  side: 'shipper' | 'partner';
}) {
  const { t } = useTranslation();
  const amount = (charge: OrderMoneyStaffSummary['charges'][number]) =>
    parsePaise(side === 'shipper' ? charge.shipperAmount : charge.partnerAmount);
  const included = summary.charges.filter((c) => c.status === 'APPROVED' && amount(c) > 0n);
  const pending = summary.charges.filter((c) => c.status === 'PENDING_APPROVAL' && amount(c) > 0n);
  return (
    <details>
      <summary>{t('payments.orderPaymentsPanel.showChargesBreakdown')}</summary>
      <p>{t('payments.orderPaymentsPanel.approvedChargesIncluded')}</p>
      {included.map((c) => (
        <p key={c.id}>
          {codeLabel('chargeType', c.type)} · {c.description || 'No description'} ·{' '}
          {formatMoney(amount(c))}
        </p>
      ))}
      {!included.length && <p>{t('payments.orderPaymentsPanel.noApprovedCharges')}</p>}
      {pending.length > 0 && (
        <>
          <p>{t('payments.orderPaymentsPanel.pendingAdjustments')}</p>
          {pending.map((c) => (
            <p key={c.id}>
              {codeLabel('chargeType', c.type)} · {c.description || 'No description'} ·{' '}
              {formatMoney(amount(c))} · {t('payments.orderPaymentsPanel.awaitingApproval')}
            </p>
          ))}
        </>
      )}
    </details>
  );
}
function Deadline({
  due,
  durationMinutes,
  done,
}: {
  due: string | null;
  durationMinutes?: number;
  done: boolean;
}) {
  const { t } = useTranslation();
  if (!due) return <>—</>;
  const left = Date.parse(due) - Date.now();
  const state: PaymentSlaState =
    left < 0
      ? 'BREACHED'
      : durationMinutes != null && left <= (durationMinutes * 60000) / 4
        ? 'AT_RISK'
        : 'ON_TRACK';
  return (
    <span className={styles.deadlineValue}>
      <span>{t('payments.orderPaymentsPanel.istTime', { time: formatIstDateTime(due) })}</span>
      {!done && <SlaChip state={state} dueAt={due} />}
    </span>
  );
}
export function OrderPaymentsPanel({
  summary,
  isManager,
  shipper,
  shipperIdentityLoading = false,
  shipperIdentityError = false,
  onRetryShipperIdentity,
  vehicleNumber,
  partnerId,
  detention,
  onAddCharge,
  onApproveCharge,
  onReviewCharge,
  onRejectCharge,
  onVoidCharge,
  onSetPartnerBill,
  actionPending = false,
  actionError = null,
}: {
  summary: OrderMoneyStaffSummary;
  isManager: boolean;
  shipper?: { name?: string | null; gstin?: string | null; creditDays?: number | null };
  shipperIdentityLoading?: boolean;
  shipperIdentityError?: boolean;
  onRetryShipperIdentity?: () => void;
  vehicleNumber?: string | null;
  partnerId?: number | null;
  /** Server detention shown read-only in invoice prep (dnm-work#165). */
  detention?: Detention | null;
  onAddCharge: () => void;
  onReviewCharge?: (chargeId: number) => void;
  onApproveCharge?: (id: number) => void;
  onRejectCharge?: (id: number) => void;
  onVoidCharge?: (id: number) => void;
  onSetPartnerBill?: () => void;
  actionPending?: boolean;
  actionError?: string | null;
}) {
  const { t } = useTranslation();
  const terms = useQuery({ queryKey: ['payments', 'settings'], queryFn: getPaymentTerms });
  const [payout, setPayout] = useState(false);
  const [paymentId, setPaymentId] = useState<number | null>(null);
  const [billCopied, setBillCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const payments = summary.payments ?? [];
  // Staff order payment amounts already include active TDS; do not add it again.
  const pendingPayouts = payments.filter(
    (payment) =>
      payment.direction === 'OUT' &&
      ['PENDING_APPROVAL', 'APPROVED'].includes(payment.status) &&
      parsePaise(payment.amount) > 0n,
  );
  const pendingAmount = summary.payments
    ? pendingPayouts.reduce((total, payment) => total + parsePaise(payment.amount), 0n)
    : maxZero(parsePaise(summary.committedToPartner) - parsePaise(summary.paidToPartner));
  const requestedUnpaid = pendingPayouts
    .filter((p) => p.status === 'PENDING_APPROVAL')
    .reduce((sum, p) => sum + parsePaise(p.amount), 0n);
  const approvedUnpaid = pendingPayouts
    .filter((p) => p.status === 'APPROVED')
    .reduce((sum, p) => sum + parsePaise(p.amount), 0n);
  const gst = summary.estimatedGst;
  const chargeReady = ['LOADED', 'IN_TRANSIT', 'AT_DROP', 'DELIVERED', 'COMPLETED'].includes(
    summary.orderStatus,
  );
  const applicableAdvancePct = orderAdvancePercentage({
    shipmentId: summary.shipmentId,
    partnerFreight: summary.partnerFreight,
    advanceCap: summary.advanceCap,
  });
  const afterPod = Boolean(summary.podVerifiedAt) || summary.orderStatus === 'COMPLETED';
  const advanceTotal = (statuses: string[]) =>
    summary.allocations.reduce((sum, allocation) => {
      const payment = payments.find((item) => item.paymentId === allocation.paymentId);
      return allocation.direction === 'OUT' &&
        allocation.purpose === 'ADVANCE' &&
        allocation.status === 'ACTIVE' &&
        payment &&
        statuses.includes(payment.status)
        ? sum + parsePaise(allocation.amount) + parsePaise(allocation.tdsAmount)
        : sum;
    }, 0n);
  const subtotal = parsePaise(summary.shipperTotal);
  const margin = summary.margin.startsWith('-')
    ? -parsePaise(summary.margin.slice(1))
    : parsePaise(summary.margin);
  const numerator = (margin < 0n ? -margin : margin) * 10000n;
  const denominator = subtotal < 0n ? -subtotal : subtotal;
  const absolutePercent =
    denominator === 0n
      ? 0n
      : numerator / denominator + (2n * (numerator % denominator) >= denominator ? 1n : 0n);
  const marginPct =
    subtotal === 0n
      ? '—'
      : `${margin < 0n && absolutePercent > 0n ? '-' : ''}${absolutePercent / 100n}.${String(absolutePercent % 100n).padStart(2, '0')} %`;
  const renderPanel = ({
    invoiceActions,
    sharedActions,
    content,
  }: {
    invoiceActions: ReactNode;
    sharedActions: ReactNode;
    content: ReactNode;
  }) => (
    <div className={styles.body}>
      {summary.legacy && (
        <div className={styles.warning}>{t('payments.orderPaymentsPanel.legacy')}</div>
      )}
      <header className={`${styles.card} ${styles.orderHeader}`}>
        <div>
          <h3>
            <OrderLabel
              shipmentId={summary.shipmentId}
              route={summary.route}
              vehicleNumber={vehicleNumber}
            />
          </h3>
          <div className={styles.orderStatus}>
            <span>
              {t('payments.orderPaymentsPanel.partnerStatus')}{' '}
              <StatusChip
                displayStatus={
                  parsePaise(summary.partnerFreight) === 0n ? 'NO_AWARD' : summary.partnerStatus
                }
              />
            </span>
            <span>
              {t('payments.orderPaymentsPanel.shipperStatus')} <StatusChip displayStatus={summary.shipperStatus} />
            </span>
          </div>
        </div>
      </header>
      <div className={styles.ledgerGrid}>
        <section className={`${styles.card} ${styles.ledgerColumn}`}>
          <h3>{t('payments.orderPaymentsPanel.customerTitle')}</h3>
          {shipperIdentityLoading && <p role="status">{t('payments.orderPaymentsPanel.loadingShipper')}</p>}
          {shipperIdentityError && (
            <p role="alert">
              {t('payments.orderPaymentsPanel.shipperUnavailable')}{' '}
              <button onClick={onRetryShipperIdentity}>{t('payments.orderPaymentsPanel.retryShipper')}</button>
            </p>
          )}
          <strong>{shipper?.name ?? '—'}</strong>
          <dl className={styles.ledger}>
            <LedgerRow label={t('payments.orderPaymentsPanel.ledger.gstin')}>{shipper?.gstin || '—'}</LedgerRow>
            <LedgerRow label={t('payments.orderPaymentsPanel.ledger.creditDays')}>
              {shipper?.creditDays === null
                ? (terms.data?.defaultShipperPaymentTermsDays ?? '—')
                : (shipper?.creditDays ?? '—')}
            </LedgerRow>
          </dl>
          <section className={styles.ledgerSection} aria-label={t('payments.orderPaymentsPanel.customerAmounts')}>
            <h4>{t('payments.orderPaymentsPanel.amounts')}</h4>
            <dl className={styles.ledger}>
              <LedgerRow label={t('payments.orderPaymentsPanel.ledger.baseFreight')}>{money(summary.shipperFreight)}</LedgerRow>
              <LedgerRow label={t('payments.orderPaymentsPanel.ledger.charges')}>
                {money(summary.shipperCharges)}
                <ChargeBreakdown summary={summary} side="shipper" />
              </LedgerRow>
              <LedgerRow label={t('payments.orderPaymentsPanel.ledger.preTaxSubtotal')}>{money(summary.shipperTotal)}</LedgerRow>
              <LedgerRow label={t('payments.orderPaymentsPanel.ledger.gstEst')}>
                {gst
                  ? `${gst.rate} % · ${money(gst.mode === 'RCM' ? gst.rcmGstAmount : gst.totalTaxAmount)}`
                  : '—'}
              </LedgerRow>
              {gst?.mode === 'RCM' && (
                <div className={styles.subtle}>
                  {t('payments.orderPaymentsPanel.rcmNote')}
                </div>
              )}
              <LedgerRow
                label={
                  summary.issuedInvoice
                    ? 'Shipper outstanding (invoiced)'
                    : gst?.mode === 'RCM'
                      ? 'Shipper outstanding (GST under RCM, paid by shipper)'
                      : 'Shipper outstanding (incl. est. GST)'
                }
              >
                {money(summary.shipperOutstanding)}
              </LedgerRow>
              <LedgerRow label={t('payments.orderPaymentsPanel.ledger.settled')}>{money(summary.settledByShipper)}</LedgerRow>
            </dl>
          </section>
          <section className={styles.ledgerSection} aria-label={t('payments.orderPaymentsPanel.customerDeadlines')}>
            <h4>{t('payments.orderPaymentsPanel.paymentDeadlines')}</h4>
            <dl className={styles.ledger}>
              <LedgerRow label={t('payments.orderPaymentsPanel.ledger.invoiceDueDate')}>
                {summary.collectionDueDate ? formatIstDate(summary.collectionDueDate) : '—'}
              </LedgerRow>
            </dl>
          </section>
          <div className={styles.orderActions}>{invoiceActions}</div>
          {content}
        </section>
        <section className={`${styles.card} ${styles.ledgerColumn}`}>
          <div className={styles.sectionTitle}>
            <h3>{t('payments.orderPaymentsPanel.partnerTitle')}</h3>
            {summary.payeeInfo && (
              <StatusChip displayStatus={`KYC_${summary.payeeInfo.kycStatus}`} />
            )}
          </div>
          <div className={summary.payeeInfo?.payeeEligible ? styles.neutralPayee : styles.warning}>
            <strong>{summary.payeeInfo?.companyName ?? '—'}</strong>
            {summary.payeeInfo?.payeeEligible ? (
              <div>
                {summary.payeeInfo.maskedAccount || '—'} · {t('payments.orderPaymentsPanel.ifsc')} {summary.payeeInfo.ifscCode || '—'}
              </div>
            ) : (
              <div>
                {reasons[summary.payeeInfo?.blockReason ?? ''] ?? 'Payee details unavailable'}
              </div>
            )}
            {!summary.payeeInfo?.payeeEligible && (
              <PayeeResolution
                partnerId={partnerId}
                reason={summary.payeeInfo?.blockReason}
                kycStatus={summary.payeeInfo?.kycStatus}
                legacy={summary.legacy}
              />
            )}
            <div className={styles.subtle}>
              {t('payments.orderPaymentsPanel.partnerBill', { number: summary.partnerBillNumber ?? t('payments.orderPaymentsPanel.notAdded') })}
            </div>
          </div>
          <div className={styles.orderActions}>
            {!summary.legacy && onSetPartnerBill && (
              <Button variant="secondary" size="sm" onClick={onSetPartnerBill}>
                {t('payments.orderPaymentsPanel.partnerBillDetails')}
              </Button>
            )}
            {summary.partnerBillNumber && summary.payeeInfo && (
              <Button
                variant="secondary"
                size="sm"
                onClick={async () => {
                  try {
                    await copyText(
                      partnerBillCopyText({
                        order: `#${summary.shipmentId}`,
                        partner: summary.payeeInfo!.companyName,
                        billNumber: summary.partnerBillNumber!,
                        route: summary.route,
                        amount: money(summary.partnerTotal),
                      }),
                    );
                    setCopyError(null);
                    setBillCopied(true);
                  } catch {
                    setBillCopied(false);
                    setCopyError('Could not copy. Check clipboard access and try again.');
                  }
                }}
              >
                {t('payments.orderPaymentsPanel.copyBillDetails')}
              </Button>
            )}
          </div>
          {billCopied && <p role="status">{t('payments.orderPaymentsPanel.billCopied')}</p>}
          {copyError && <p role="alert">{copyError}</p>}

          <section className={styles.ledgerSection} aria-label={t('payments.orderPaymentsPanel.partnerAmounts')}>
            <h4>{t('payments.orderPaymentsPanel.amounts')}</h4>
            <dl className={styles.ledger}>
              <LedgerRow label={t('payments.orderPaymentsPanel.ledger.baseFreight')}>{money(summary.partnerFreight)}</LedgerRow>
              <LedgerRow label={t('payments.orderPaymentsPanel.ledger.charges')}>
                {money(summary.partnerCharges)}
                <ChargeBreakdown summary={summary} side="partner" />
              </LedgerRow>
              <LedgerRow label={t('payments.orderPaymentsPanel.ledger.partnerTotal')}>{money(summary.partnerTotal)}</LedgerRow>
              <LedgerRow label={t('payments.orderPaymentsPanel.ledger.paid')}>{money(summary.paidToPartner)}</LedgerRow>
              <LedgerRow label={t('payments.orderPaymentsPanel.ledger.pendingPayment')}>
                {formatMoney(
                  maxZero(parsePaise(summary.partnerTotal) - parsePaise(summary.paidToPartner)),
                )}
              </LedgerRow>
              {afterPod &&
                maxZero(parsePaise(summary.partnerTotal) - parsePaise(summary.paidToPartner)) >
                  0n && (
                  <p className={styles.subtle}>
                    {t('payments.orderPaymentsPanel.unpaidAdvanceFolded')}
                  </p>
                )}
            </dl>
            <details>
              <summary>{t('payments.orderPaymentsPanel.viewPaymentBreakdown')}</summary>
              <dl className={styles.ledger}>
                <LedgerRow
                  label={
                    applicableAdvancePct == null ? 'Advance' : `Advance (${applicableAdvancePct} %)`
                  }
                >
                  {money(summary.advanceCap)}
                </LedgerRow>
                <LedgerRow label={t('payments.orderPaymentsPanel.ledger.advancePaid')}>
                  {summary.payments ? formatMoney(advanceTotal(['PAID'])) : '—'}
                </LedgerRow>
                <LedgerRow label={t('payments.orderPaymentsPanel.ledger.advanceRemaining')}>{money(summary.advanceRemaining)}</LedgerRow>
                <LedgerRow
                  label={
                    afterPod ? 'Balance remaining (incl. folded advance)' : 'Balance remaining'
                  }
                >
                  {afterPod
                    ? money(summary.partnerRemaining)
                    : formatMoney(
                        parsePaise(summary.partnerRemaining) - parsePaise(summary.advanceRemaining),
                      )}
                  {!afterPod && <span className={styles.subtle}>{t('payments.orderPaymentsPanel.dueAfterPod')}</span>}
                </LedgerRow>
                {!afterPod && (
                  <LedgerRow label={t('payments.orderPaymentsPanel.ledger.partnerRemaining')}>{money(summary.partnerRemaining)}</LedgerRow>
                )}
              </dl>
            </details>
          </section>
          <section className={styles.ledgerSection} aria-label={t('payments.orderPaymentsPanel.partnerDeadlines')}>
            <h4>{t('payments.orderPaymentsPanel.paymentDeadlines')}</h4>
            {afterPod && <p className={styles.subtle}>{t('payments.orderPaymentsPanel.foldedAtPod')}</p>}
            <dl className={styles.ledger}>
              {!afterPod && (
                <LedgerRow label={t('payments.orderPaymentsPanel.ledger.advanceDueDate')}>
                  {summary.legacy ? (
                    '—'
                  ) : (
                    <Deadline
                      due={summary.advanceDueBy}
                      durationMinutes={terms.data?.partnerAdvanceSlaMinutes}
                      done={Boolean(summary.advanceDoneAt)}
                    />
                  )}
                </LedgerRow>
              )}
              <LedgerRow label={t('payments.orderPaymentsPanel.ledger.balanceDueDate')}>
                {summary.legacy ? (
                  '—'
                ) : (
                  <Deadline
                    due={summary.balanceDueBy}
                    durationMinutes={
                      terms.data ? terms.data.partnerBalanceSlaHours * 60 : undefined
                    }
                    done={Boolean(summary.balanceDoneAt)}
                  />
                )}
              </LedgerRow>
            </dl>
            <div className={styles.orderActions}>
              {!summary.legacy && (
                <Button
                  variant="primary"
                  size="sm"
                  disabled={
                    !chargeReady ||
                    partnerId == null ||
                    summary.orderStatus === 'CANCELLED' ||
                    parsePaise(summary.partnerFreight) === 0n
                  }
                  onClick={() => setPayout(true)}
                >
                  {t('payments.orderPaymentsPanel.newPayout')}
                </Button>
              )}
            </div>
            {!summary.legacy && !chargeReady && (
              <p className={styles.subtle}>{t('payments.orderPaymentsPanel.payoutsAfterLoading')}</p>
            )}
            {!summary.legacy && pendingAmount > 0n && (
              <div className={styles.subtle} aria-label={t('payments.orderPaymentsPanel.unpaidWorkflow')}>
                {summary.payments ? (
                  <>
                    {requestedUnpaid > 0n && (
                      <p>{t('payments.orderPaymentsPanel.awaitingApprovalAmount', { amount: formatMoney(requestedUnpaid) })}</p>
                    )}
                    {approvedUnpaid > 0n && (
                      <p>
                        {t('payments.orderPaymentsPanel.approvedAwaitingPayment', { amount: formatMoney(approvedUnpaid) })}
                      </p>
                    )}
                  </>
                ) : (
                  <p>
                    {t('payments.orderPaymentsPanel.pendingPayout', { amount: formatMoney(pendingAmount) })}
                  </p>
                )}
                <p>{t('payments.orderPaymentsPanel.reservationsNote')}</p>
              </div>
            )}
          </section>
        </section>
      </div>
      <section className={styles.orderActions} aria-label={t('payments.orderPaymentsPanel.wholeOrderActions')}>
        <span className={styles.subtle}>{t('payments.orderPaymentsPanel.wholeOrderActions')}</span>
        {sharedActions}
      </section>
      {isManager && (
        <div className={`${styles.card} ${styles.marginSummary}`} aria-label={t('payments.orderPaymentsPanel.marginSummary')}>
          <dl className={styles.ledger}>
            <LedgerRow label={t('payments.orderPaymentsPanel.ledger.margin')}>{formatMoney(margin)}</LedgerRow>
            <LedgerRow label={t('payments.orderPaymentsPanel.ledger.marginPct')}>{marginPct}</LedgerRow>
          </dl>
        </div>
      )}
      <OrderPaymentHistory payments={payments} direction="OUT" onOpen={setPaymentId} />
      <OrderPaymentHistory payments={payments} direction="IN" onOpen={setPaymentId} />
      <div className={styles.card}>
        <div className={styles.sectionTitle}>
          <h3>{t('payments.orderPaymentsPanel.extraCharges')}</h3>
          {!summary.legacy && (
            <Button
              variant="secondary"
              size="sm"
              disabled={!chargeReady}
              title={chargeReady ? undefined : 'Available after loading'}
              onClick={onAddCharge}
            >
              {t('payments.orderPaymentsPanel.addCharge')}
            </Button>
          )}
        </div>
        {actionError && (
          <div role="alert" className={styles.error}>
            {actionError}
          </div>
        )}
        {summary.charges.length === 0 ? (
          <p>{t('payments.orderPaymentsPanel.noExtraCharges')}</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('payments.orderPaymentsPanel.colType')}</th>
                  <th>{t('payments.orderPaymentsPanel.colDescription')}</th>
                  <th>{t('payments.orderPaymentsPanel.colShipper')}</th>
                  <th>{t('payments.orderPaymentsPanel.colPartner')}</th>
                  <th>{t('payments.orderPaymentsPanel.colStatus')}</th>
                  {isManager && !summary.legacy && <th>{t('payments.orderPaymentsPanel.colActions')}</th>}
                </tr>
              </thead>
              <tbody>
                {summary.charges.map((charge) => (
                  <tr key={charge.id}>
                    <td>{charge.type}</td>
                    <td>
                      {charge.requestedByUserId != null && (
                        <div>
                          {t('payments.orderPaymentsPanel.partnerRequest', {
                            name:
                              charge.requestedByName ??
                              t('payments.orderPaymentsPanel.partnerFallback', { id: charge.requestedByUserId }),
                            amount: money(charge.requestedPartnerAmount ?? charge.partnerAmount),
                          })}
                        </div>
                      )}
                      {charge.requestedReason ?? charge.description ?? '—'}
                      {charge.requestedByUserId != null &&
                        charge.status === 'PENDING_APPROVAL' &&
                        onReviewCharge && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => onReviewCharge(charge.id)}
                          >
                            {t('payments.orderPaymentsPanel.viewRequest')}
                          </Button>
                        )}
                      {charge.adjustmentNote && <div>{t('payments.orderPaymentsPanel.adjustment', { note: charge.adjustmentNote })}</div>}
                    </td>
                    <td>{money(charge.shipperAmount)}</td>
                    <td>{money(charge.partnerAmount)}</td>
                    <td>{codeLabel('chargeStatus', charge.status)}</td>
                    {isManager && !summary.legacy && (
                      <td>
                        <div className={styles.orderActions}>
                          {charge.status === 'PENDING_APPROVAL' && (
                            <>
                              <Button
                                variant="primary"
                                size="sm"
                                disabled={actionPending}
                                onClick={() => onApproveCharge?.(charge.id)}
                              >
                                {t('payments.orderPaymentsPanel.approve')}
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                disabled={actionPending}
                                onClick={() => onRejectCharge?.(charge.id)}
                              >
                                {t('payments.orderPaymentsPanel.reject')}
                              </Button>
                            </>
                          )}
                          {charge.status === 'APPROVED' && (
                            <button
                              disabled={actionPending}
                              onClick={() => onVoidCharge?.(charge.id)}
                            >
                              {t('payments.orderPaymentsPanel.void')}
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <OrderPaymentDialogs
        key={summary.shipmentId}
        shipmentId={summary.shipmentId}
        partnerId={partnerId}
        advanceTerms={{
          shipmentId: summary.shipmentId,
          partnerFreight: summary.partnerFreight,
          advanceCap: summary.advanceCap,
        }}
        payeeName={summary.payeeInfo?.companyName}
        isManager={isManager}
        readOnly={summary.legacy}
        payout={payout && !summary.legacy}
        paymentId={paymentId}
        onClosePayout={() => setPayout(false)}
        onClosePayment={() => setPaymentId(null)}
      />
    </div>
  );
  return (
    <InvoiceBox
      summary={summary}
      isManager={isManager}
      gstin={shipper?.gstin}
      detention={detention}
      render={renderPanel}
    />
  );
}
